import { Injectable, NgZone, inject } from '@angular/core';
import posthog from 'posthog-js';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
@Injectable({ providedIn: 'root' })
export class PosthogService {
  private readonly ngZone = inject(NgZone);

  private flagsLoaded = false;

  constructor() {
    this.initPostHog();
  }
  private initPostHog() {
    if (environment.posthogApiKey) {
      this.ngZone.runOutsideAngular(() => {
        posthog.init(environment.posthogApiKey, {
          api_host: environment.posthogHost,
          debug: false,
          cross_subdomain_cookie: true,
          capture_pageview: 'history_change'
        });
      });
    }
  }

  setCompanyGroup(agdOrgId?: string | number | null, companyName?: string) {
    if (!environment.posthogApiKey || !agdOrgId) return;
    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.group('company', String(agdOrgId), {
          name: companyName ?? 'Unknown Company',
          id: agdOrgId
        });

        posthog.register({ orgId: agdOrgId });

        if (companyName) {
          posthog.register({ company: companyName });
        }
      } catch (error) {
        console.error('Failed to set PostHog company group:', error);
      }
    });
  }

  identifyUser(
    userId: string,
    agdOrgId?: string | number | null,
    properties?: { email?: string; name?: string; [key: string]: unknown }
  ) {
    if (!environment.posthogApiKey || !userId) return;
    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.identify(userId, properties);

        if (properties?.email) {
          posthog.register({ email: properties.email });
        }

        if (agdOrgId) {
          posthog.register({ orgId: agdOrgId });
        }
      } catch (error) {
        console.error('Failed to identify user in PostHog:', error);
      }
    });
  }

  captureEvent(eventName: string, properties?: Record<string, unknown>) {
    if (!environment.posthogApiKey) return;
    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.capture(eventName, properties);
      } catch (error) {
        console.error('Failed to capture PostHog event:', error);
      }
    });
  }

  getFeatureFlag(
    key: string,
    enabledWithoutAnalytics = true
  ): Observable<boolean> {
    if (!environment.posthogApiKey) {
      return of(enabledWithoutAnalytics);
    }

    if (this.flagsLoaded && posthog) {
      return of(posthog.isFeatureEnabled(key) ?? false);
    }

    return new Observable<boolean>((observer) => {
      if (!posthog) {
        observer.next(false);
        observer.complete();
        return;
      }

      posthog.onFeatureFlags(() => {
        this.flagsLoaded = true;
        observer.next(posthog.isFeatureEnabled(key) ?? false);
        observer.complete();
      });

      setTimeout(() => {
        if (!observer.closed) {
          console.warn('Timed out during feature flag retrieval from PostHog');
          observer.next(false);
          observer.complete();
        }
      }, 3000);
    });
  }
}
